// @flow strict

/**
 * @fileoverview Handles routing; default export is the application's routing component
 * @summary Routing module
 * @see util/routing.js
 */

import { ConnectedRouter } from 'connected-react-router'
import { history } from '../store/store'
import { logger } from '../util/logger'
import { Provider } from 'react-redux'
import { Route, Switch, withRouter } from 'react-router-dom'
import * as React from 'react'
import App from '../containers/App'
import Colors from '../assets/colors.json'

// Types
import type { ContextRouter } from 'react-router-dom'
import type { State as Store } from '../store/store'

// Util/Constants /////////////////////////////////////////////////////////////

// Lazy loaded routes
const DeviceRoutes = React.lazy(() => import('./device'))
const LocationRoutes = React.lazy(() => import('./location'))
const MiscRoutes = React.lazy(() => import('./misc'))
const UserRoutes = React.lazy(() => import('./user'))

/** Whether or not the current environment supports push history */
const supportsHistory: boolean = 'pushState' in window.history

/**
 * @summary Get route prefix
 * @description This should only be used while transitioning to One Ring
 * @param  {string} url - Current URL
 * @return {string} - What to prefix routes with
 */
export const getRoutePrefix = (url: string): string =>
  /^\/locations\//.test(url)
    ? '/locations/:locationId/devices/:deviceId'
    : '/devices/:locationId'

/** generic page styles for reuse */
export const genericStyles = {
  modalSlideIn: {
    zIndex: 1300,
    backgroundColor: Colors.white,
    opacity: 0.985,
    position: 'absolute',
    top: 0
  }
}

/**
 * Determine if this url is for a deep link
 * @return {Boolean} - Whether or not this URL is part of a deep link
 */
const isDeepLink = (): boolean => {
  const DEEP_LINK_HOSTNAME = 'app-deep-link.rach.io'
  return window.location.hostname === DEEP_LINK_HOSTNAME
}

/* eslint-disable require-jsdoc */
/**
 * 404 page
 * @returns {JSX} - 404 page
 */
export class NotFound extends React.Component<{}> {
  /** Create and log error */
  componentDidMount() {
    const err = new Error('ROUTE NOT FOUND')
    // $FlowFixMe
    err.requestedURL = window.location.href
    logger.error(err, { URL: window.location.href })
  }

  /**
   * Render
   * @return {JSX} - 404 component
   */
  render() {
    return (
      <section>
        {isDeepLink() ? (
          <h1 style={{ fontSize: '126px', textAlign: 'center' }}>
            {'Please open this link on your mobile device'}
          </h1>
        ) : (
          <h1 style={{ fontSize: '126px', textAlign: 'center' }}>
            {'Page not found'}
          </h1>
        )}
      </section>
    )
  }
}
/* eslint-enable require-jsdoc */

/**
 * Main App and router wrapper
 * @returns {JSX} - <App><Routes /></App>
 */
const AppWrapper = withRouter(
  ({
    location,
    match
  }: {
    location: $PropertyType<ContextRouter, 'location'>,
    match: $PropertyType<ContextRouter, 'match'>
  }) => (
    <App>
      <React.Suspense fallback={<div />}>
        <Switch location={location}>
          <Route path={'/locations'} component={LocationRoutes} />
          <Route path={'/devices'}>
            <DeviceRoutes location={location} match={match} />
          </Route>
          <Route path={'/users'} component={UserRoutes} />
          <Route path={'/'} component={MiscRoutes} />
        </Switch>
      </React.Suspense>
    </App>
  )
)

// Router /////////////////////////////////////////////////////////////////////

/**
 * Router
 * @param {object} props - Component properties
 * @param {object} props.Store - Redux Store
 * @param {object} props.history - Redux Router `history`
 * @returns {JSX} - Routing component
 */
const CustomRouter = (props: { Store: Store }) => (
  <Provider store={props.Store}>
    <ConnectedRouter
      history={history}
      forceRefresh={!supportsHistory}
      keyLength={12}
    >
      <AppWrapper />
    </ConnectedRouter>
  </Provider>
)

export default CustomRouter
